import React, { useState, useEffect, useMemo } from "react";
import dayjs from "dayjs";
import {
    Area,
    ComposedChart,
    CartesianGrid,
    Line,
    LabelList,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { isMobileViewport } from "../../utils/responsive";
import { getPaddedDomain } from "../../utils/chartDomain";
import { formatCompactNumberKR } from "../../utils/numberFormat";
import { getThemeColors } from "../../utils/themeColors";
import FilterPill from "../common/filter_pill";
import Skeleton from "../common/skeleton";
import SectionError from "./section_error";

const RANGE_OPTIONS = [
    { key: "3m", label: "3M", months: 3 },
    { key: "6m", label: "6M", months: 6 },
    { key: "1y", label: "1Y", months: 12 },
    { key: "all", label: "전체", months: null },
];

// 기간 토글은 재조회 없이 클라이언트에서 슬라이스한다 — points는 전체 이력을 한 번만 받는다.
const filterByRange = (points, rangeKey) => {
    if (!points.length) return points;
    const option = RANGE_OPTIONS.find((o) => o.key === rangeKey);
    if (!option || option.months === null) return points;
    const lastDate = dayjs(points[points.length - 1].date);
    const cutoff = lastDate.subtract(option.months, "month");
    return points.filter((p) => !dayjs(p.date).isBefore(cutoff));
};

// 데이터 전 구간을 담는 최소 RANGE_OPTIONS 값을 산출한다 — 기본 노출 범위를 6M 고정 대신 실제 이력 길이에 맞춘다.
const computeDefaultRangeKey = (allPoints) => {
    if (!allPoints || allPoints.length === 0) return RANGE_OPTIONS[0].key;
    const firstDate = dayjs(allPoints[0].date);
    const lastDate = dayjs(allPoints[allPoints.length - 1].date);
    const covering = RANGE_OPTIONS.find((option) => {
        if (option.months === null) return true;
        const cutoff = lastDate.subtract(option.months, "month");
        return !firstDate.isBefore(cutoff);
    });
    return covering ? covering.key : RANGE_OPTIONS[RANGE_OPTIONS.length - 1].key;
};

const formatKrwAxis = (v) => `₩${formatCompactNumberKR(v)}`;
const formatKrwTooltip = (v) => `₩${Math.round(Number(v) || 0).toLocaleString()}`;

/** 마지막 데이터 포인트에만 값 라벨을 그리는 LabelList content 렌더러. */
const makeEndLabel = (dataLength, color) => (props) => {
    const { x, y, index, value } = props;
    if (index !== dataLength - 1 || value == null) return null;
    return (
        <text
            x={x}
            y={y}
            dx={6}
            dy={-8}
            fill={color}
            fontSize={11}
            textAnchor="start"
            className="font-num"
        >
            {`₩${formatCompactNumberKR(value)}`}
        </text>
    );
};

/**
 * AccumulationChart — ② 적립 성과 차트.
 * 누적 투입원금(계단형 점선) vs 평가액(area+line) 겹침, 기간 토글(3M/6M/1Y/전체).
 *
 * datasetKey — 계좌 필터(P1-E7) 등으로 points가 다른 시리즈로 통째 교체될 때 부모가 넘기는 식별자
 * (예: selectedAccountId). 값이 바뀌면 자동 range 산출을 다시 트리거한다 (검수 M-5: autoRangeApplied가
 * 데이터셋 교체를 감지하지 못해 계좌를 바꿔도 이전 range에 고정되는 결함 수정).
 * snapshotSince/accountFallback — 계좌별 조회 응답의 개별 기록 시작일·전체 합계 폴백 여부(P1-E7).
 */
function AccumulationChart({
    points,
    loading,
    error,
    onRetry,
    datasetKey = 0,
    snapshotSince,
    accountFallback,
    // 도메인마다 "왜 이 앞 구간이 비어 있는가"의 이유가 다르다. 구조·축·색은 공유하고
    // 문구만 갈아끼운다 (DAS-177 — BTC 적립이 같은 컴포넌트를 재사용한다).
    emptyMessage = "아직 누적된 매수 기록이 없습니다.",
    dataStartSuffix = "이전 구간은 증권사 API 미제공",
}) {
    const [rangeKey, setRangeKey] = useState("6m");
    const [autoRangeApplied, setAutoRangeApplied] = useState(false);
    const [lastDatasetKey, setLastDatasetKey] = useState(datasetKey);
    const C = getThemeColors();
    const isMobile = isMobileViewport();

    const basePoints = useMemo(() => (Array.isArray(points) ? points : []), [points]);

    // 데이터셋이 교체되면(계좌 필터 전환 등) 자동 range를 다시 계산할 수 있도록 잠금을 푼다 (M-5).
    useEffect(() => {
        if (datasetKey === lastDatasetKey) return;
        setLastDatasetKey(datasetKey);
        setAutoRangeApplied(false);
    }, [datasetKey, lastDatasetKey]);

    // 데이터가 처음 도착했을 때(또는 데이터셋이 교체됐을 때) 한 번만 기본 range를 전 구간 커버
    // 최소값으로 맞춘다 — 이후 사용자의 수동 토글은 덮어쓰지 않는다.
    useEffect(() => {
        if (autoRangeApplied || basePoints.length === 0) return;
        setRangeKey(computeDefaultRangeKey(basePoints));
        setAutoRangeApplied(true);
    }, [basePoints, autoRangeApplied]);

    if (loading) {
        return (
            <div className="rounded-2xl border border-surface-border bg-surface-card p-3 shadow-card sm:p-4">
                <Skeleton variant="line" width="30%" height={14} />
                <Skeleton variant="rect" height={isMobile ? 220 : 320} className="mt-3" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="rounded-2xl border border-surface-border bg-surface-card p-3 shadow-card sm:p-4">
                <SectionError message="적립 성과 차트를 불러오지 못했습니다." onRetry={onRetry} />
            </div>
        );
    }

    const displayData = filterByRange(basePoints, rangeKey);
    const domain = getPaddedDomain(
        displayData
            .flatMap((p) => [Number(p.principal_krw), Number(p.value_krw)])
            .filter((v) => Number.isFinite(v))
    );
    const height = isMobile ? 240 : 340;
    const yAxisWidth = isMobile ? 56 : 72;
    // 데이터 포인트가 1~2개뿐이면 선이 사실상 안 보이므로 Area·Line 양쪽에 점을 켠다.
    const showEndDots = displayData.length <= 2;

    const rangeToggle = (
        <div className="flex items-center gap-1.5">
            {RANGE_OPTIONS.map((option) => (
                <FilterPill
                    key={option.key}
                    active={option.key === rangeKey}
                    onClick={() => {
                        setRangeKey(option.key);
                        setAutoRangeApplied(true);
                    }}
                    variant="solid"
                >
                    {option.label}
                </FilterPill>
            ))}
        </div>
    );

    // 계좌별 기록 시작 각주. snapshotSince가 있으면(특정 계좌 선택 + 정상 응답) 계좌 전용 문구,
    // 없으면 기존 전체 합계 문구(첫 기록일 기준, 증권사 API 미제공 고지)를 쓴다 (P1-E7).
    const dataStartFootnote = snapshotSince ? (
        <div className="mt-3 text-xs text-content-muted">
            이 계좌는 {dayjs(snapshotSince).format("YYYY-MM-DD")}부터 개별 기록됩니다. 이전 구간은 전체 합계로만 조회할 수 있습니다.
        </div>
    ) : basePoints.length > 0 ? (
        <div className="mt-3 text-xs text-content-muted">
            {dayjs(basePoints[0].date).format("YYYY-MM-DD")}부터 기록 시작 — {dataStartSuffix}
        </div>
    ) : null;

    // 계좌별 조회가 비어 전체 합계로 폴백했을 때의 안내 (P1-E7, 강등 모드·snapshot_since 이전 공통 처리).
    const accountFallbackNotice = accountFallback ? (
        <div className="mt-2 rounded-lg bg-warning-subtle px-3 py-2 text-xs text-warning">
            계좌별 기록 준비 중 — 전체 합계로 표시합니다.
        </div>
    ) : null;

    // 빈 상태 ①: 전체 매수 기록이 아예 없음 — 기간 토글도 의미가 없으므로 숨긴다.
    if (basePoints.length === 0) {
        return (
            <div className="rounded-2xl border border-surface-border bg-surface-card p-3 shadow-card sm:p-4">
                <div className="text-sm font-semibold text-content-secondary">적립 성과 차트</div>
                {accountFallbackNotice}
                <div className="mt-6 py-8 text-center text-xs text-content-muted">
                    {emptyMessage}
                </div>
            </div>
        );
    }

    // 빈 상태 ②: 기록이 1건뿐이라 추이선을 그릴 수 없음 — 기간 토글은 렌더(basePoints.length>0).
    if (basePoints.length === 1) {
        return (
            <div className="rounded-2xl border border-surface-border bg-surface-card p-3 shadow-card sm:p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="text-sm font-semibold text-content-secondary">적립 성과 차트</div>
                    {rangeToggle}
                </div>
                {accountFallbackNotice}
                <div className="mt-6 py-8 text-center text-xs text-content-muted">
                    기록 축적 중 — 추이는 2일 차부터 표시됩니다.
                </div>
                {dataStartFootnote}
            </div>
        );
    }

    // 빈 상태 ③: 전체 기록은 있으나 선택한 기간 내에는 0건 — 기간 토글로 탈출 가능.
    if (displayData.length === 0) {
        return (
            <div className="rounded-2xl border border-surface-border bg-surface-card p-3 shadow-card sm:p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="text-sm font-semibold text-content-secondary">적립 성과 차트</div>
                    {rangeToggle}
                </div>
                {accountFallbackNotice}
                <div className="mt-6 py-8 text-center text-xs text-content-muted">
                    선택한 기간에 기록이 없습니다.
                </div>
                {dataStartFootnote}
            </div>
        );
    }

    return (
        <div className="rounded-2xl border border-surface-border bg-surface-card p-3 shadow-card sm:p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm font-semibold text-content-secondary">적립 성과 차트</div>
                {rangeToggle}
            </div>
            {accountFallbackNotice}
            <div className="mt-2 flex items-center gap-3 text-xs text-content-muted">
                <span className="inline-flex items-center gap-1">
                    <span className="inline-block h-0.5 w-3 rounded-full" style={{ backgroundColor: C.content }} aria-hidden="true" />
                    평가액
                </span>
                <span className="inline-flex items-center gap-1">
                    <span className="inline-block h-0.5 w-3 rounded-full border-t border-dashed" style={{ borderColor: C.contentSecondary }} aria-hidden="true" />
                    누적 투입원금
                </span>
            </div>
            <div className="mt-3" style={{ height }}>
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={displayData} margin={{ top: 10, right: isMobile ? 40 : 60, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={C.surfaceElevated} vertical={false} />
                        <XAxis
                            dataKey="date"
                            tick={{ fontSize: 12, fill: C.contentSecondary }}
                            tickFormatter={(v) => dayjs(v).format("MM-DD")}
                            tickLine={false}
                            axisLine={false}
                            minTickGap={24}
                        />
                        <YAxis
                            width={yAxisWidth}
                            tick={{ fontSize: 12, fill: C.contentMuted }}
                            tickFormatter={formatKrwAxis}
                            tickLine={false}
                            axisLine={false}
                            domain={domain}
                        />
                        <Tooltip
                            formatter={(value, name) => [
                                formatKrwTooltip(value),
                                name === "value_krw" ? "평가액" : "누적 투입원금",
                            ]}
                            labelFormatter={(v) => dayjs(v).format("YYYY-MM-DD")}
                            contentStyle={{
                                backgroundColor: C.surfaceElevated,
                                border: `1px solid ${C.surfaceBorder}`,
                                borderRadius: "0.75rem",
                            }}
                            labelStyle={{ color: C.contentSecondary }}
                            itemStyle={{ color: C.content }}
                        />
                        <defs>
                            <linearGradient id="stockValueGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={C.content} stopOpacity={0.25} />
                                <stop offset="100%" stopColor={C.content} stopOpacity={0.02} />
                            </linearGradient>
                        </defs>
                        <Area
                            type="monotone"
                            dataKey="value_krw"
                            stroke={C.content}
                            strokeWidth={2}
                            fill="url(#stockValueGradient)"
                            dot={showEndDots ? { r: 3, fill: C.content, strokeWidth: 0 } : false}
                            isAnimationActive={false}
                        >
                            <LabelList dataKey="value_krw" content={makeEndLabel(displayData.length, C.content)} />
                        </Area>
                        <Line
                            type="stepAfter"
                            dataKey="principal_krw"
                            stroke={C.contentSecondary}
                            strokeWidth={1.6}
                            strokeDasharray="4 4"
                            dot={showEndDots ? { r: 3, fill: C.contentSecondary, strokeWidth: 0 } : false}
                            isAnimationActive={false}
                        >
                            <LabelList dataKey="principal_krw" content={makeEndLabel(displayData.length, C.contentSecondary)} />
                        </Line>
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
            {dataStartFootnote}
        </div>
    );
}

export default AccumulationChart;
